<template>
  <header>
    <div class="background">
      <template v-if="!isMobile">
        <div class="top-nav">
          <div class="logo-container" @click="goHome">
            <img src="@/assets/logo.png" alt="ReuBar Logo" class="logo-image" />
            <div class="logo-text">
              <span class="logo-name">ReuBar</span>
              <span class="company-name">Barron Heating AC, Electrical & Plumbing</span>
            </div>
          </div>

          <div class="desktop-spacer-leading"></div>

          <div class="modules-container">
            <div
              v-for="item in visibleModules"
              :key="item.name"
              class="nav-tile"
              :class="{ 
                active: activeModule === item.name,
                hovered: hoveredTile === item.name
              }"
              @click.prevent="onModuleClick(item)"
              @mouseenter="hoveredTile = item.name"
              @mouseleave="hoveredTile = null"
            >
              <span class="tile-label">{{ item.label }}</span>
            </div>
          </div>

          <div class="desktop-spacer-trailing"></div>
        </div>
      </template>

      <template v-else>
        <!-- mobile top row: logo + text on one line -->
        <div class="mobile-header">
          <img src="@/assets/logo.png" alt="ReuBar Logo" class="mobile-logo-image" />
          <span class="mobile-logo-name">ReuBar</span>
          <span class="mobile-company-name">Barron Heating&nbsp;AC, Electrical & Plumbing</span>
        </div>

        <!-- mobile module buttons row -->
        <div class="mobile-modules">
          <div
            v-for="item in visibleModules"
            :key="item.name"
            class="nav-tile"
            :class="{ 
              active: activeModule === item.name,
              hovered: hoveredTile === item.name
            }"
            @click.prevent="onModuleClick(item)"
            @mouseenter="hoveredTile = item.name"
            @mouseleave="hoveredTile = null"
          >
            <span class="tile-label">{{ item.label }}</span>
          </div>
        </div>
      </template>
    </div>
  </header>
</template>


<script setup>
import { ref, inject, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const isMobile = inject('isMobile')
const auth = inject('auth')
const sideMenuState = inject('sideMenuState')

const route = useRoute()
const router = useRouter()
const hoveredTile = ref(null)


const modules = [
  { name: 'operations', label: 'Operations', path: '/operations', requiredPermission: 'operations.general.access'},
  { name: 'inventory', label: 'Inventory', path: '/inventory', requiredPermission: 'inventory.general.access' },
  { name: 'sales_marketing', label: 'Sales & Marketing', path: '/sales-marketing', requiredPermission: 'sales_marketing.general.access' },
  { name: 'accounting', label: 'Accounting', path: '/accounting', requiredPermission: 'accounting.general.access' },
  { name: 'admin', label: 'Admin', path: '/admin', requiredPermission: 'admin.general.access' }
]

const activeModule = computed(() => {
  if (!route.name) return null
  return route.name.split('-')[0] // "inventory", "operations", "admin"
})

const visibleModules = computed(() =>
  modules.filter(m => !m.requiredPermission || auth.hasPermission(m.requiredPermission))
)

function onModuleClick(item) {
  if (activeModule.value !== item.name) {
    router.push(item.path)
  }
  sideMenuState.expand()
}

const goHome = () => {
  router.push({ name: 'home' })
}
</script>

<style scoped>
/* Prevent horizontal scrolling */
html, body {
  margin: 0;
  padding: 0;
  overflow-x: hidden; /* Prevent horizontal overflow */
  width: 100%;
}

.background {
  background-color: #ffffff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

/* Top nav bar */
.top-nav {
  width: 100%;
  padding: 0.5rem 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
}

.desktop-spacer-leading {
  width: 3rem;
}

.desktop-spacer-trailing {
  display: flex;
  flex-grow: 1;
}

.modules-container {
  display: flex;
  justify-content: space-evenly;
  /* flex-grow: 1; */
  gap: 0.8rem; /* or 1rem if you want more space */
}

/* Override nav-tile styling just in mobile */
.mobile-modules .nav-tile {
  flex: 0 0 auto;            /* prevent stretching full width */
  min-width: auto;           /* shrink to content size */
  padding: 0.25rem 0.6rem;   /* reduce padding */
  font-size: 0.8rem;         /* slightly smaller text */
  border-radius: 4px;        /* smaller corners */
}

.nav-tile {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #ffffff;
  background-color: #1e538d;
  padding: 0.3rem 1.25rem;
  border-radius: 6px;
  font-weight: 500;
  transition: background-color 0.2s ease, color 0.2s ease;
}

/* Hover (non-active) */
.nav-tile.hovered:not(.active) {
  background-color: #163d6b; /* Darker blue */
}

/* Active state */
.nav-tile.active {
  background-color: #daa521; /* Gold */
  color: #000000; /* Black text */
  font-weight: bold;
}

/* Active + Hovered (optional deeper gold shade) */
.nav-tile.active.hovered {
  background-color: #c1931d; /* Darker gold */
}

.logo-container {
  display: flex;
  align-items: center; /* vertically align logo and text */
  cursor: pointer;
}

.logo-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1e538d;
  display: block; /* ensures block layout for stacking */
}

.logo-image {
  height: 40px;
  width: 40px; /* Set width equal to height for perfect circle */
  margin-right: 0.75rem;
  border-radius: 20%; /* Makes it circular */
  object-fit: cover; /* Ensures image fills the circle */
}

.logo-text {
  display: flex;
  flex-direction: column;
  line-height: 1.2;
}

.logo-name {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e538d;
}

.company-name {
  font-size: 0.75rem;
  font-weight: 400;
  color: #555;
  margin-top: 2px;
}

/* --- mobile variant --- */
.mobile-header {
  display: flex;
  align-items: center;
  gap: .4rem;
  padding: 0.2rem .5rem 0rem 1rem;
  cursor: pointer;
}

.mobile-logo-image {
  height: 14px;
  width: 14px;
  border-radius: 6px;
}

.mobile-logo-name {
  font-size: 0.7rem;
  font-weight: 700;
  color: #1e538d;
}

.mobile-company-name {
  font-size: .65rem;
  color: #555;
  white-space: nowrap;      /* keep in one line */
  overflow: hidden;
  text-overflow: ellipsis;  /* truncate if too long */
}

.mobile-modules {
  display: flex;
  justify-content: center;     /* center items horizontally */
  gap: 0.6rem;                   /* space between buttons */
  padding: 0.2rem 1rem;        /* adjust as needed */
  flex-wrap: wrap;             /* allow wrapping if too many buttons */
}

.tile-label { text-align: center; }

</style>
